/**
 * Hanging Protocols MVHP — motor puro (pick → resolve slots).
 * Reutiliza Prior Matching para slots role=prior.
 */

import {
  buildSeriesMapForStudy,
  seriesMetaFromFiles,
  studyUidFromFile,
  suggestPriorSeries,
  planeNormalFromIop,
  PRIOR_MATCH_MIN_SCORE,
} from './comparePriorStudy'

/**
 * @typedef {{
 *   modality?: string|null,
 *   description?: string[]|null,
 *   exclude?: string[]|null,
 *   usePriorMatching?: boolean,
 *   minScore?: number,
 *   requireDifferentPlane?: boolean,
 * }} HangingSeriesRule
 */

/**
 * @typedef {{
 *   viewportIndex: number,
 *   role: 'current'|'prior',
 *   series?: HangingSeriesRule,
 *   pairFromSlot?: number,
 * }} HangingSlot
 */

/**
 * @typedef {{
 *   id: string,
 *   label: string,
 *   priority?: number,
 *   match: { modalities?: string[], studyDescription?: string[] },
 *   layout: [number, number],
 *   compareMode?: boolean,
 *   linkSeries?: boolean,
 *   slots: HangingSlot[],
 * }} HangingProtocol
 */

/**
 * @typedef {{
 *   viewportIndex: number,
 *   role: string,
 *   seriesFiles: object[]|null,
 *   seriesKey: string|null,
 *   paired: boolean,
 *   status: 'paired'|'unpaired'|'empty',
 *   reason?: string,
 * }} HangingSlotResolution
 */

const EXCL_SCOUT = ['scout', 'localizer', 'topogram', 'doc']
const CHEST_DESC = ['chest', 'torax', 'tórax', 'pulm', 'thorax', 'lung']
const SPINE_DESC = [
  'spine',
  'columna',
  'lumbar',
  'cervical',
  'dorsal',
  'l-spine',
  'c-spine',
]
const BRAIN_DESC = ['brain', 'cerebro', 'crane', 'skull', 'head']
const XR_MODS = ['CR', 'DX', 'XR']

/**
 * @param {HangingProtocol|null|undefined} protocol
 * @returns {boolean}
 */
export function protocolHasPriorSlot(protocol) {
  const slots = protocol && Array.isArray(protocol.slots) ? protocol.slots : []
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] && slots[i].role === 'prior') return true
  }
  return false
}

/** @type {HangingProtocol[]} */
export const HANGING_SEEDS = [
  // —— CT: current ——
  {
    id: 'ct-chest-1x1',
    label: 'CT Tórax 1×1',
    priority: 88,
    match: { modalities: ['CT'], studyDescription: CHEST_DESC },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'CT',
          description: ['axial', 'ax ', 'helical', 'lung', 'chest'],
          exclude: EXCL_SCOUT,
        },
      },
    ],
  },
  {
    id: 'ct-chest-1x2',
    label: 'CT Tórax 1×2 series',
    priority: 92,
    match: { modalities: ['CT'], studyDescription: CHEST_DESC },
    layout: [1, 2],
    compareMode: false,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'CT',
          description: ['axial', 'ax ', 'lung', 'chest'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'CT',
          description: ['cor', 'sag', 'mip', 'mediast'],
          exclude: EXCL_SCOUT,
          requireDifferentPlane: true,
        },
      },
    ],
  },
  {
    id: 'ct-generic-1x1',
    label: 'CT 1×1',
    priority: 42,
    match: { modalities: ['CT'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'CT', exclude: EXCL_SCOUT },
      },
    ],
  },
  {
    id: 'ct-generic-1x2',
    label: 'CT 1×2 series',
    priority: 38,
    match: { modalities: ['CT'], studyDescription: [] },
    layout: [1, 2],
    compareMode: false,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'CT', exclude: EXCL_SCOUT },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'CT',
          description: ['cor', 'sag', 'mip', 'reformat', 'mpr'],
          exclude: EXCL_SCOUT,
          requireDifferentPlane: true,
        },
      },
    ],
  },
  // —— CT: prior ——
  {
    id: 'ct-chest-1x2-prior',
    label: 'CT Tórax + prior 1×2',
    priority: 100,
    match: { modalities: ['CT'], studyDescription: CHEST_DESC },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'CT',
          description: ['axial', 'ax ', 'helical', 'lung', 'chest'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  {
    id: 'ct-generic-1x2-prior',
    label: 'CT + prior 1×2',
    priority: 40,
    match: { modalities: ['CT'], studyDescription: [] },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'CT', exclude: EXCL_SCOUT },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  // —— MR: current ——
  {
    id: 'mr-spine-1x2',
    label: 'MR Columna 1×2',
    priority: 90,
    match: { modalities: ['MR'], studyDescription: SPINE_DESC },
    layout: [1, 2],
    compareMode: false,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['sag', 't1'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['sag', 't2'],
          exclude: EXCL_SCOUT,
        },
      },
    ],
  },
  {
    id: 'mr-brain-1x2',
    label: 'MR Cerebro 1×2',
    priority: 89,
    match: { modalities: ['MR'], studyDescription: BRAIN_DESC },
    layout: [1, 2],
    compareMode: false,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['ax', 't1'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['ax', 't2', 'flair'],
          exclude: EXCL_SCOUT,
        },
      },
    ],
  },
  {
    id: 'mr-generic-1x1',
    label: 'MR 1×1',
    priority: 42,
    match: { modalities: ['MR'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'MR', exclude: EXCL_SCOUT },
      },
    ],
  },
  {
    id: 'mr-generic-1x2',
    label: 'MR 1×2 series',
    priority: 38,
    match: { modalities: ['MR'], studyDescription: [] },
    layout: [1, 2],
    compareMode: false,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'MR', exclude: EXCL_SCOUT },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['t2', 'flair', 'sag', 'cor'],
          exclude: EXCL_SCOUT,
          requireDifferentPlane: true,
        },
      },
    ],
  },
  // —— MR: prior ——
  {
    id: 'mr-spine-2x2-prior',
    label: 'MR Columna + prior 2×2',
    priority: 95,
    match: { modalities: ['MR'], studyDescription: SPINE_DESC },
    layout: [2, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['sag', 't1'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
      {
        viewportIndex: 4,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['sag', 't2'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 5,
        role: 'prior',
        pairFromSlot: 4,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  {
    id: 'mr-brain-2x2-prior',
    label: 'MR Cerebro + prior 2×2',
    priority: 94,
    match: { modalities: ['MR'], studyDescription: BRAIN_DESC },
    layout: [2, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['ax', 't1'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
      {
        viewportIndex: 4,
        role: 'current',
        series: {
          modality: 'MR',
          description: ['ax', 't2', 'flair'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 5,
        role: 'prior',
        pairFromSlot: 4,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  {
    id: 'mr-generic-1x2-prior',
    label: 'MR + prior 1×2',
    priority: 40,
    match: { modalities: ['MR'], studyDescription: [] },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'MR', exclude: EXCL_SCOUT },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  // —— XR / CR / DX ——
  {
    id: 'xr-chest-1x1',
    label: 'RX Tórax 1×1',
    priority: 85,
    match: {
      modalities: XR_MODS,
      studyDescription: ['chest', 'torax', 'tórax', 'thorax', 'rx'],
    },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          description: ['pa', 'ap', 'chest', 'torax', 'tórax'],
          exclude: ['scout'],
        },
      },
    ],
  },
  {
    id: 'xr-generic-1x1',
    label: 'RX/DX 1×1',
    priority: 36,
    match: { modalities: XR_MODS, studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { exclude: ['scout'] },
      },
    ],
  },
  {
    id: 'xr-chest-1x2-prior',
    label: 'RX Tórax + prior 1×2',
    priority: 90,
    match: {
      modalities: XR_MODS,
      studyDescription: ['chest', 'torax', 'tórax', 'thorax', 'rx'],
    },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          description: ['pa', 'ap', 'chest', 'torax', 'tórax'],
          exclude: ['scout'],
        },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  {
    id: 'xr-generic-1x2-prior',
    label: 'RX/DX + prior 1×2',
    priority: 40,
    match: { modalities: XR_MODS, studyDescription: [] },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { exclude: ['scout'] },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  // —— MG ——
  {
    id: 'mg-generic-1x2',
    label: 'MG CC/MLO 1×2',
    priority: 50,
    match: { modalities: ['MG'], studyDescription: [] },
    layout: [1, 2],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: {
          modality: 'MG',
          description: ['cc', 'cranio'],
          exclude: EXCL_SCOUT,
        },
      },
      {
        viewportIndex: 1,
        role: 'current',
        series: {
          modality: 'MG',
          description: ['mlo', 'oblic'],
          exclude: EXCL_SCOUT,
        },
      },
    ],
  },
  {
    id: 'mg-generic-1x1',
    label: 'MG 1×1',
    priority: 36,
    match: { modalities: ['MG'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'MG', exclude: EXCL_SCOUT },
      },
    ],
  },
  {
    id: 'mg-generic-1x2-prior',
    label: 'MG + prior 1×2',
    priority: 55,
    match: { modalities: ['MG'], studyDescription: [] },
    layout: [1, 2],
    compareMode: true,
    linkSeries: true,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'MG', exclude: EXCL_SCOUT },
      },
      {
        viewportIndex: 1,
        role: 'prior',
        pairFromSlot: 0,
        series: { usePriorMatching: true, minScore: PRIOR_MATCH_MIN_SCORE },
      },
    ],
  },
  // —— US / PT ——
  {
    id: 'us-generic-1x1',
    label: 'US 1×1',
    priority: 36,
    match: { modalities: ['US'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { modality: 'US', exclude: EXCL_SCOUT },
      },
    ],
  },
  {
    id: 'pt-generic-1x1',
    label: 'PT 1×1',
    priority: 36,
    match: { modalities: ['PT', 'NM'], studyDescription: [] },
    layout: [1, 1],
    compareMode: false,
    linkSeries: false,
    slots: [
      {
        viewportIndex: 0,
        role: 'current',
        series: { exclude: EXCL_SCOUT },
      },
    ],
  },
]

/**
 * @param {string|null|undefined} text
 * @param {string[]|null|undefined} needles
 * @returns {boolean}
 */
export function textMatchesAny(text, needles) {
  const hay = String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (!Array.isArray(needles) || !needles.length) return true
  for (let i = 0; i < needles.length; i++) {
    const n = String(needles[i] || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
    if (n && hay.indexOf(n) >= 0) return true
  }
  return false
}

/**
 * @param {object|null|undefined} f
 * @returns {string}
 */
function modalityFromFile(f) {
  if (!f) return ''
  const series = f.series || {}
  const st = f.study || {}
  return String(
    series.modality ||
      series.Modality ||
      f.modality ||
      st.modality ||
      ''
  )
    .trim()
    .toUpperCase()
}

/**
 * @param {object|null|undefined} f
 * @returns {string}
 */
function studyDescriptionFromFile(f) {
  if (!f) return ''
  const st = f.study || {}
  return String(
    st.studyDescription || st.description || f.studyDescription || ''
  ).trim()
}

/**
 * @param {object[]|null|undefined} studyFiles
 * @returns {{ modality: string, studyDescription: string, studyUid: string }}
 */
export function studyMetaFromFiles(studyFiles) {
  const list = Array.isArray(studyFiles) ? studyFiles.filter(Boolean) : []
  const sample = list[0] || null
  let modality = modalityFromFile(sample)
  let studyDescription = studyDescriptionFromFile(sample)
  if (!modality || !studyDescription) {
    for (let i = 0; i < list.length; i++) {
      if (!modality) modality = modalityFromFile(list[i])
      if (!studyDescription) studyDescription = studyDescriptionFromFile(list[i])
      if (modality && studyDescription) break
    }
  }
  return {
    modality,
    studyDescription,
    studyUid: studyUidFromFile(sample),
  }
}

/**
 * Score 0–100 de aplicabilidad del protocolo al estudio.
 * @param {HangingProtocol} protocol
 * @param {{ modality?: string, studyDescription?: string }} studyMeta
 * @returns {number}
 */
export function scoreProtocolMatch(protocol, studyMeta) {
  if (!protocol || !protocol.match) return 0
  const mods = Array.isArray(protocol.match.modalities)
    ? protocol.match.modalities.map((m) => String(m).toUpperCase())
    : []
  const mod = String((studyMeta && studyMeta.modality) || '')
    .trim()
    .toUpperCase()
  if (mods.length && mod && mods.indexOf(mod) < 0) return 0
  if (mods.length && !mod) return 0

  let score = 20
  if (mods.length && mod && mods.indexOf(mod) >= 0) score += 40

  const descNeedles = protocol.match.studyDescription
  if (Array.isArray(descNeedles) && descNeedles.length) {
    if (
      textMatchesAny(
        (studyMeta && studyMeta.studyDescription) || '',
        descNeedles
      )
    ) {
      score += 40
    } else {
      // Modalidad ok pero descripción no → score bajo (solo seeds genéricos ganan)
      score -= 15
    }
  } else {
    score += 5 // genérico
  }

  const prio =
    typeof protocol.priority === 'number' ? protocol.priority : 0
  return Math.max(0, Math.min(100, score)) + prio / 1000
}

/**
 * @param {{ modality?: string, studyDescription?: string }} studyMeta
 * @param {HangingProtocol[]|null|undefined} [seeds]
 * @returns {HangingProtocol[]}
 */
export function listCompatibleProtocols(studyMeta, seeds = HANGING_SEEDS) {
  const list = Array.isArray(seeds) ? seeds : HANGING_SEEDS
  return list
    .map((p) => ({ p, s: scoreProtocolMatch(p, studyMeta) }))
    .filter((x) => x.s >= 40)
    .sort((a, b) => b.s - a.s || String(a.p.id).localeCompare(String(b.p.id)))
    .map((x) => x.p)
}

/**
 * @param {{ modality?: string, studyDescription?: string }} studyMeta
 * @param {HangingProtocol[]|null|undefined} [seeds]
 * @param {{ preferPrior?: boolean }} [opts]
 * @returns {HangingProtocol|null}
 */
export function pickProtocol(studyMeta, seeds = HANGING_SEEDS, opts = {}) {
  const compatible = listCompatibleProtocols(studyMeta, seeds)
  if (!compatible.length) return null
  const preferPrior = !!(opts && opts.preferPrior)
  const ranked = compatible.slice().sort((a, b) => {
    const ap = protocolHasPriorSlot(a) ? 1 : 0
    const bp = protocolHasPriorSlot(b) ? 1 : 0
    if (preferPrior) {
      if (bp !== ap) return bp - ap
    } else if (ap !== bp) {
      return ap - bp
    }
    const as = scoreProtocolMatch(a, studyMeta)
    const bs = scoreProtocolMatch(b, studyMeta)
    return bs - as || String(a.id).localeCompare(String(b.id))
  })
  return ranked[0] || null
}

/**
 * |dot| de normales de plano; ~1 = mismo plano.
 * @param {number[]|null|undefined} iopA
 * @param {number[]|null|undefined} iopB
 * @returns {number|null}
 */
export function planeAlignment(iopA, iopB) {
  const nA = planeNormalFromIop(iopA)
  const nB = planeNormalFromIop(iopB)
  if (!nA || !nB) return null
  return Math.abs(nA[0] * nB[0] + nA[1] * nB[1] + nA[2] * nB[2])
}

/**
 * Elige la mejor serie del estudio actual según regla de slot.
 * @param {object[]} studyFiles
 * @param {HangingSeriesRule|null|undefined} rule
 * @param {{
 *   excludeSeriesKeys?: Set<string>|string[]|null,
 *   referenceIop?: number[]|null,
 * }} [opts]
 * @returns {{ seriesFiles: object[], seriesKey: string, meta: object }|null}
 */
export function pickCurrentSeriesForSlot(studyFiles, rule, opts = {}) {
  const list = Array.isArray(studyFiles) ? studyFiles.filter(Boolean) : []
  if (!list.length) return null
  const excludeKeys = new Set()
  const rawEx = opts && opts.excludeSeriesKeys
  if (rawEx && typeof rawEx.forEach === 'function') {
    rawEx.forEach((k) => {
      if (k) excludeKeys.add(String(k))
    })
  }
  const refIop = (opts && opts.referenceIop) || null
  const requireDiff = !!(rule && rule.requireDifferentPlane)
  const { seriesList, seriesKeys } = buildSeriesMapForStudy(list)
  let best = null
  let bestScore = -1
  for (let i = 0; i < seriesKeys.length; i++) {
    const key = seriesKeys[i]
    if (excludeKeys.has(key)) continue
    const seriesFiles = seriesList.get(key) || []
    const meta = seriesMetaFromFiles(seriesFiles)
    if (!meta || meta.isReport) continue
    let score = 10
    if (rule && rule.modality) {
      const want = String(rule.modality).toUpperCase()
      if (meta.modality !== want) continue
      score += 30
    }
    if (rule && Array.isArray(rule.exclude) && rule.exclude.length) {
      if (textMatchesAny(meta.seriesDescription, rule.exclude)) continue
      if (meta.isScout && textMatchesAny('scout', rule.exclude)) continue
    }
    const descNeedles =
      rule && Array.isArray(rule.description) ? rule.description : null
    const descHit =
      descNeedles && descNeedles.length
        ? textMatchesAny(meta.seriesDescription, descNeedles)
        : true
    if (descNeedles && descNeedles.length) {
      if (descHit) score += 40
      else score -= 5
    }
    if (requireDiff) {
      const align = planeAlignment(refIop, meta.imageOrientationPatient)
      if (align != null) {
        if (align >= 0.92) continue // mismo plano (p.ej. axial crudo + REF axial)
        score += Math.round((1 - align) * 35)
      } else if (!descHit) {
        // Sin IOP ni descripción alternativa → no rellenar con otra axial “parecida”
        continue
      }
    }
    if (meta.isScout) score -= 20
    if (score > bestScore) {
      bestScore = score
      best = { seriesFiles, seriesKey: key, meta }
    }
  }
  return best
}

/**
 * @param {HangingProtocol|null|undefined} protocol
 * @returns {number}
 */
export function countCurrentSlots(protocol) {
  const slots = protocol && Array.isArray(protocol.slots) ? protocol.slots : []
  let n = 0
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] && slots[i].role === 'current') n += 1
  }
  return n
}

/**
 * @param {HangingSlotResolution[]|null|undefined} resolved
 * @returns {number}
 */
export function countPairedCurrentSlots(resolved) {
  const list = Array.isArray(resolved) ? resolved : []
  let n = 0
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].role === 'current' && list[i].status === 'paired') {
      n += 1
    }
  }
  return n
}

/**
 * Elige protocolo; si es multi-serie current y no hay 2º plano distinto → 1×1.
 * @param {{ modality?: string, studyDescription?: string }} studyMeta
 * @param {object[]} currentStudyFiles
 * @param {HangingProtocol[]|null|undefined} [seeds]
 * @param {{ preferPrior?: boolean }} [opts]
 * @returns {HangingProtocol|null}
 */
export function pickProtocolForStudy(
  studyMeta,
  currentStudyFiles,
  seeds = HANGING_SEEDS,
  opts = {}
) {
  const primary = pickProtocol(studyMeta, seeds, opts)
  if (!primary) return null
  const need = countCurrentSlots(primary)
  if (need <= 1 || (opts && opts.preferPrior)) return primary
  const probed = resolveSlots(primary, currentStudyFiles, currentStudyFiles || [])
  if (countPairedCurrentSlots(probed) >= need) return primary
  const list = Array.isArray(seeds) ? seeds : HANGING_SEEDS
  const mono = list
    .filter(
      (p) =>
        p &&
        !protocolHasPriorSlot(p) &&
        (p.layout[0] || 1) * (p.layout[1] || 1) === 1 &&
        scoreProtocolMatch(p, studyMeta) >= 40
    )
    .sort(
      (a, b) =>
        scoreProtocolMatch(b, studyMeta) - scoreProtocolMatch(a, studyMeta)
    )
  return mono[0] || primary
}

/**
 * Resuelve series por slot del protocolo.
 * @param {HangingProtocol} protocol
 * @param {object[]} currentStudyFiles
 * @param {object[]} allFiles
 * @param {{ priorStudyUid?: string|null }} [opts]
 * @returns {HangingSlotResolution[]}
 */
export function resolveSlots(protocol, currentStudyFiles, allFiles, opts = {}) {
  if (!protocol || !Array.isArray(protocol.slots)) return []
  /** @type {Map<number, HangingSlotResolution>} */
  const byVp = new Map()
  const slots = protocol.slots
  const usedKeys = new Set()
  let referenceIop = null

  // 1) current slots (sin reutilizar serie; plano distinto si la regla lo pide)
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]
    if (!slot || slot.role !== 'current') continue
    const picked = pickCurrentSeriesForSlot(currentStudyFiles, slot.series, {
      excludeSeriesKeys: usedKeys,
      referenceIop,
    })
    const vp = slot.viewportIndex
    if (picked && picked.seriesFiles && picked.seriesFiles.length) {
      usedKeys.add(picked.seriesKey)
      if (
        !referenceIop &&
        picked.meta &&
        picked.meta.imageOrientationPatient
      ) {
        referenceIop = picked.meta.imageOrientationPatient
      }
      byVp.set(vp, {
        viewportIndex: vp,
        role: 'current',
        seriesFiles: picked.seriesFiles,
        seriesKey: picked.seriesKey,
        paired: true,
        status: 'paired',
        reason: 'current_match',
      })
    } else {
      byVp.set(vp, {
        viewportIndex: vp,
        role: 'current',
        seriesFiles: null,
        seriesKey: null,
        paired: false,
        status: 'empty',
        reason: 'no_current_series',
      })
    }
  }

  // 2) prior slots (delegate to Prior Matching)
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]
    if (!slot || slot.role !== 'prior') continue
    const vp = slot.viewportIndex
    const pairFrom =
      typeof slot.pairFromSlot === 'number' ? slot.pairFromSlot : 0
    const ref = byVp.get(pairFrom)
    const refFiles =
      ref && Array.isArray(ref.seriesFiles) ? ref.seriesFiles : null
    if (!refFiles || !refFiles.length) {
      byVp.set(vp, {
        viewportIndex: vp,
        role: 'prior',
        seriesFiles: null,
        seriesKey: null,
        paired: false,
        status: 'unpaired',
        reason: 'no_reference',
      })
      continue
    }
    const minScore =
      slot.series && slot.series.minScore != null
        ? Number(slot.series.minScore)
        : PRIOR_MATCH_MIN_SCORE
    const sug = suggestPriorSeries(refFiles, allFiles || [], {
      primaryStudyUid: studyUidFromFile(refFiles[0]),
      priorStudyUid: opts.priorStudyUid || undefined,
      minScore,
    })
    if (sug && Array.isArray(sug.seriesFiles) && sug.seriesFiles.length) {
      byVp.set(vp, {
        viewportIndex: vp,
        role: 'prior',
        seriesFiles: sug.seriesFiles,
        seriesKey: sug.seriesKey,
        paired: true,
        status: 'paired',
        reason: `prior_score:${sug.score}`,
      })
    } else {
      byVp.set(vp, {
        viewportIndex: vp,
        role: 'prior',
        seriesFiles: null,
        seriesKey: null,
        paired: false,
        status: 'unpaired',
        reason: 'no_prior_match',
      })
    }
  }

  return slots.map((s) => {
    const vp = s.viewportIndex
    return (
      byVp.get(vp) || {
        viewportIndex: vp,
        role: s.role || 'current',
        seriesFiles: null,
        seriesKey: null,
        paired: false,
        status: 'empty',
        reason: 'missing',
      }
    )
  })
}

/**
 * @param {string} protocolId
 * @param {HangingProtocol[]|null|undefined} [seeds]
 * @returns {HangingProtocol|null}
 */
export function getProtocolById(protocolId, seeds = HANGING_SEEDS) {
  const id = protocolId != null ? String(protocolId).trim() : ''
  if (!id) return null
  const list = Array.isArray(seeds) ? seeds : HANGING_SEEDS
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].id === id) return list[i]
  }
  return null
}
